<template>
  <div class="max-w-7xl mx-auto text-xs">
    <div class="flex items-center justify-between gap-4 mb-4">
      <div class="flex items-center gap-3">
        <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
          <button
            v-for="t in tabs" :key="t.key" type="button"
            :class="['px-3 py-1.5 font-medium', tab === t.key ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50']"
            @click="tab = t.key"
          >{{ t.label }} ({{ t.count }})</button>
        </div>
        <input v-model="query" type="search" placeholder="Search pages, widgets, sources" class="border border-gray-300 rounded-md px-3 py-1.5 w-72" />
      </div>
    </div>

    <div v-if="tab === 'pages'" class="flex items-center gap-4 mb-3 text-gray-500">
      <span><font-awesome-icon :icon="['fas', 'globe']" class="text-primary-600 mr-1" />Public</span>
      <span><font-awesome-icon :icon="['fas', 'user']" class="text-gray-700 mr-1" />Just yours</span>
      <span><font-awesome-icon :icon="['fas', 'lock']" class="text-gray-400 mr-1" />Private to someone else</span>
      <span><font-awesome-icon :icon="['fas', 'share-from-square']" class="text-emerald-600 mr-1" />On the external portal</span>
      <span class="flex-1" />
      <span v-for="origin in ['platform', 'stock', 'custom']" :key="origin" :class="badge(origin)">{{ origin }} widget</span>
    </div>
    <LoadingIndicator v-if="loading" message="Loading inventory..." />
    <p v-if="error" class="text-red-700">{{ error }}</p>

    <div v-if="!loading && tab === 'pages'" class="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
      <template v-for="group in pageGroups" :key="`group-${group.stem}`">
      <div class="flex items-center gap-2 px-4 py-2 bg-gray-50 font-semibold text-gray-700">
        <font-awesome-icon :icon="['fas', 'folder']" class="text-gray-400" />
        <span :class="{ sensitive: group.stem }" data-spoof-kind="label">{{ group.label }}</span>
        <span class="font-normal text-gray-400">{{ group.pages.length }}</span>
      </div>
      <div v-for="page in group.pages" :id="`page-${page.key}`" :key="page.key">
        <button type="button" class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50" @click="toggle(page.key)">
          <font-awesome-icon :icon="['fas', open[page.key] ? 'chevron-down' : 'chevron-right']" class="w-3 h-3 text-gray-400 shrink-0" />
          <span class="w-5 text-center shrink-0" :title="visibility(page).title">
            <font-awesome-icon :icon="['fas', visibility(page).icon]" :class="visibility(page).tone" />
          </span>
          <span class="w-5 text-center shrink-0" :title="onPortal(page) ? 'On the external portal' : ''">
            <font-awesome-icon v-if="onPortal(page)" :icon="['fas', 'share-from-square']" class="text-emerald-600" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block font-medium text-gray-900 truncate" :class="{ sensitive: page.origin === 'saved' }" data-spoof-kind="label">{{ page.title }}</span>
            <span class="block text-xs text-gray-500 truncate">
              <span :class="{ sensitive: !!page.owner }" data-spoof-kind="person">{{ page.owner }}</span>
              <template v-if="page.owner && (page.subject || page.stem)"> · </template>
              <span>{{ page.subject }}</span>
              <span v-if="page.stem" class="sensitive" data-spoof-kind="label">{{ page.subject ? ' · ' : '' }}{{ page.stem }}</span>
            </span>
          </span>
          <span class="flex flex-wrap justify-end gap-1 max-w-[46%]">
            <span v-for="placement in page.placements" :key="placement.id" :class="[badge(placement.origin), { sensitive: placement.origin === 'custom' }]" :title="`${placement.slug} · ${placement.origin}`" data-spoof-kind="label">{{ placement.name }}</span>
            <span v-if="page.layout === 'html'" :class="badge('html')">html</span>
          </span>
          <span :class="badge(page.origin)">{{ page.origin }}</span>
          <span class="text-xs text-gray-500 w-28 text-right shrink-0">{{ when(page.updated_at) }}</span>
        </button>
        <div v-if="open[page.key]" class="pl-10 pr-4 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
          <div class="flex flex-wrap items-center gap-x-6 gap-y-1 text-gray-600">
            <span v-if="page.owner"><span class="text-gray-400">Owner</span> <span class="font-medium text-gray-800 sensitive" data-spoof-kind="person">{{ page.owner }}</span></span>
            <span v-if="page.stem"><span class="text-gray-400">Folder</span> <span class="font-medium text-gray-800 sensitive" data-spoof-kind="label">{{ page.stem }}</span></span>
            <span v-if="page.subject"><span class="text-gray-400">Subject</span> <span class="font-medium text-gray-800">{{ page.subject }}</span></span>
            <span class="flex-1" />
            <router-link v-if="page.id && page.layout === 'spec'" :to="{ name: 'AdminAgentPageEdit', params: { id: page.id } }" class="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 no-underline hover:bg-gray-100">Edit</router-link>
            <router-link v-if="page.id" :to="`/pages/${page.id}`" class="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 no-underline hover:bg-gray-100">
              Open page {{ page.id }} <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="w-3 h-3 text-gray-400" />
            </router-link>
          </div>

          <div v-if="page.placements.length" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700">Widgets and the data they read</div>
            <table class="w-full">
              <thead>
                <tr class="text-left text-gray-400 border-b border-gray-100">
                  <th class="px-3 py-1.5 font-medium w-64">Widget</th>
                  <th class="px-3 py-1.5 font-medium w-40">Input</th>
                  <th class="px-3 py-1.5 font-medium">Data source</th>
                  <th class="px-3 py-1.5 font-medium">Parameters</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <template v-for="placement in page.placements" :key="placement.id">
                  <tr v-for="(input, i) in (placement.inputs.length ? placement.inputs : [null])" :key="`${placement.id}-${i}`" :class="i === 0 ? 'border-t border-gray-200' : ''">
                    <td v-if="i === 0" class="px-3 py-2 align-top bg-gray-50/60" :rowspan="Math.max(placement.inputs.length, 1)">
                      <a href="#" class="text-primary-600 no-underline font-semibold" :class="{ sensitive: placement.origin === 'custom' }" data-spoof-kind="label" @click.prevent="showWidget(placement.slug)">{{ placement.name }}</a>
                      <div class="mt-0.5 flex items-center gap-1.5 text-gray-400">
                        <span>{{ placement.slug }}</span><span :class="badge(placement.origin)">{{ placement.origin }}</span><span v-if="placement.version">v{{ placement.version }}</span>
                      </div>
                    </td>
                    <template v-if="input">
                      <td class="px-3 py-2 align-top"><code class="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{{ input.input }}</code></td>
                      <td class="px-3 py-2 align-top"><SourceCell :source="input" /></td>
                      <td class="px-3 py-2 align-top text-gray-500">{{ input.params || '—' }}</td>
                    </template>
                    <td v-else class="px-3 py-2 text-gray-500 italic" colspan="3">{{ placement.origin === 'platform' ? "Loads its own data from the page's metric, domain, entity and year" : 'No data inputs' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="page.sources.length" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700">{{ page.layout === 'html' ? 'Sources the page declares' : 'Shared page sources' }}</div>
            <table class="w-full">
              <tbody class="divide-y divide-gray-100">
                <tr v-for="source in page.sources" :key="source.name">
                  <td class="px-3 py-2 w-64 align-top"><code class="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{{ source.name }}</code></td>
                  <td class="px-3 py-2 align-top"><SourceCell :source="source" /></td>
                  <td class="px-3 py-2 align-top text-gray-500">{{ source.params || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="!page.placements.length && !page.sources.length" class="text-gray-500 italic">This page places no widgets and declares no sources.</p>
        </div>
      </div>
      </template>
      <p v-if="!visiblePages.length" class="px-4 py-3 text-gray-500">No pages match.</p>
    </div>

    <div v-if="tab === 'widgets'" class="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-gray-500">
      <span v-for="family in FAMILIES" :key="family.key" :class="['rounded px-1.5 py-0.5 font-medium', family.tone]">{{ family.label }}</span>
      <span class="inline-flex items-center gap-1.5 whitespace-nowrap"><span class="rounded bg-gray-100 text-gray-700 italic px-1.5 py-0.5">kind</span>Generic query on the page's metric or entity</span>
      <span class="inline-flex items-center gap-1.5 whitespace-nowrap"><span class="rounded border border-dashed border-gray-300 bg-white text-gray-600 italic px-1.5 py-0.5">any kind</span>Accepts this kind, none bound yet</span>
    </div>
    <div v-if="!loading && tab === 'widgets'" class="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
      <div v-for="widget in visibleWidgets" :id="`widget-${widget.key}`" :key="widget.key">
        <button type="button" class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50" @click="toggle(`w-${widget.key}`)">
          <font-awesome-icon :icon="['fas', open[`w-${widget.key}`] ? 'chevron-down' : 'chevron-right']" class="w-3 h-3 text-gray-400 shrink-0" />
          <span class="w-56 shrink-0 min-w-0">
            <span class="block font-medium text-gray-900 truncate" :class="{ sensitive: widget.origin === 'custom' }" data-spoof-kind="label">{{ widget.name }}</span>
            <span class="block text-xs text-gray-500 truncate">{{ widget.slug }}</span>
          </span>
          <span class="flex-1 min-w-0 flex flex-wrap justify-start gap-1.5 text-left">
            <template v-for="b in sourceBadges(widget).slice(0, 6)" :key="b.text">
              <span v-if="b.parts && b.parts.length" class="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50/60 px-1 py-0.5" :title="b.title">
                <span :class="['rounded px-1.5 py-0.5 font-medium sensitive', kindTone('workflow')]" data-spoof-kind="label">{{ b.text }}</span>
                <span v-for="part in b.parts" :key="part.id" :class="['rounded px-1.5 py-0.5 font-medium sensitive', kindTone('data_attribute')]" :title="`data attribute ${part.id}`" data-spoof-kind="label">{{ part.label }}</span>
              </span>
              <span
                v-else
                :class="['rounded px-1.5 py-0.5 font-medium', b.accepts ? 'border border-dashed border-gray-300 bg-white text-gray-600 italic' : kindTone(b.kind), { italic: b.generic, sensitive: !b.generic && !b.accepts }]"
                :title="b.title"
                data-spoof-kind="label"
              >{{ b.text }}</span>
            </template>
            <span v-if="sourceBadges(widget).length > 6" class="text-gray-500 py-0.5" :title="sourceBadges(widget).slice(6).map((b) => b.text).join(', ')">+{{ sourceBadges(widget).length - 6 }}</span>
            <span v-if="!sourceBadges(widget).length" class="text-gray-400">&mdash;</span>
          </span>
          <span class="w-16 shrink-0 text-left"><span :class="badge(widget.origin)">{{ widget.origin }}</span></span>
          <span class="text-xs text-gray-500 w-12 text-right shrink-0">v{{ widget.version }}</span>
          <span class="text-xs text-gray-500 w-24 text-right shrink-0">{{ widget.inputs.length }} input{{ widget.inputs.length === 1 ? '' : 's' }}</span>
          <span class="text-xs text-gray-500 w-24 text-right shrink-0">{{ widget.used_by.length }} page{{ widget.used_by.length === 1 ? '' : 's' }}</span>
          <span class="text-xs text-gray-500 w-28 text-right shrink-0">{{ when(widget.updated_at) }}</span>
        </button>
        <div v-if="open[`w-${widget.key}`]" class="pl-10 pr-4 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
          <p v-if="widget.description" class="text-gray-600 leading-relaxed max-w-4xl" :class="{ sensitive: widget.origin === 'custom' }" data-spoof-kind="label">{{ widget.description }}</p>

          <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700">Data it asks for</div>
            <p v-if="widget.loads_own_data" class="px-3 py-2 text-gray-500 italic">Loads its own data from the page's {{ widget.reads.join(', ') || 'variables' }}; no sources are bound to it.</p>
            <p v-else-if="!widget.inputs.length" class="px-3 py-2 text-gray-500 italic">No data inputs.<span v-if="widget.reads.length"> Reads {{ widget.reads.join(', ') }}.</span></p>
            <table v-else class="w-full">
              <thead>
                <tr class="text-left text-gray-400 border-b border-gray-100">
                  <th class="px-3 py-1.5 font-medium w-64">Input</th>
                  <th class="px-3 py-1.5 font-medium w-56">Accepts</th>
                  <th class="px-3 py-1.5 font-medium">Fixed query</th>
                  <th class="px-3 py-1.5 font-medium">Columns it needs</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="input in widget.inputs" :key="input.name">
                  <td class="px-3 py-2 align-top">
                    <div class="font-medium text-gray-800">{{ input.display_name || input.name }}</div>
                    <code v-if="input.display_name" class="text-gray-400">{{ input.name }}</code>
                    <div v-if="input.description" class="mt-0.5 text-gray-500">{{ input.description }}</div>
                  </td>
                  <td class="px-3 py-2 align-top">
                    <div class="flex flex-wrap gap-1">
                      <span v-for="k in input.kinds" :key="k" :class="['rounded px-1.5 py-0.5 font-medium', kindTone(k)]">{{ k }}</span>
                    </div>
                  </td>
                  <td class="px-3 py-2 align-top text-gray-500">{{ queryText(input.query) || '—' }}</td>
                  <td class="px-3 py-2 align-top text-gray-500">{{ input.columns.join(', ') || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="grid gap-3" style="grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)">
            <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700">Data sources bound across pages</div>
              <p v-if="!widget.bound_sources.length" class="px-3 py-2 text-gray-500 italic">None bound yet.</p>
              <ul v-else class="divide-y divide-gray-100">
                <li v-for="source in widget.bound_sources" :key="`${source.kind}-${source.id}-${source.label}`" class="flex items-center justify-between gap-3 px-3 py-2">
                  <SourceCell :source="source" />
                  <span class="shrink-0 text-gray-400">{{ source.pages }} placement{{ source.pages === 1 ? '' : 's' }}</span>
                </li>
              </ul>
            </div>
            <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700">Placed on</div>
              <p v-if="!widget.used_by.length" class="px-3 py-2 text-gray-500 italic">No page places it.</p>
              <ul v-else class="divide-y divide-gray-100">
                <li v-for="use in widget.used_by" :key="`${use.page_key}-${use.placement}`" class="flex items-center justify-between gap-3 px-3 py-2">
                  <a href="#" class="text-primary-600 no-underline font-medium truncate" :class="{ sensitive: !use.page_key.startsWith('built-in') }" data-spoof-kind="label" @click.prevent="showPage(use.page_key)">{{ use.page }}</a>
                  <code class="shrink-0 text-gray-400">{{ use.placement }}</code>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <p v-if="!visibleWidgets.length" class="px-4 py-3 text-gray-500">No widgets match.</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import SourceCell from './SourceCell.vue';
import { FAMILIES, GENERIC_KINDS, kindLabel, kindTone } from './sourceKinds';

export default {
  name: 'PagesAdmin',
  components: { FontAwesomeIcon, LoadingIndicator, SourceCell },
  data() {
    return { loading: false, error: '', pages: [], widgets: [], tab: 'pages', query: '', open: {}, FAMILIES };
  },
  computed: {
    tabs() {
      return [
        { key: 'pages', label: 'Pages', count: this.pages.length },
        { key: 'widgets', label: 'Widgets', count: this.widgets.length },
      ];
    },
    needle() {
      return this.query.trim().toLowerCase();
    },
    visiblePages() {
      if (!this.needle) return this.pages;
      return this.pages.filter((page) => this.pageText(page).includes(this.needle));
    },
    pageGroups() {
      const groups = new Map();
      this.visiblePages.forEach((page) => {
        const stem = page.stem || '';
        if (!groups.has(stem)) groups.set(stem, { stem, label: stem ? stem.split('/').join(' / ') : 'Not in a folder', pages: [] });
        groups.get(stem).pages.push(page);
      });
      return [...groups.values()].sort((a, b) => (a.stem === '' ? -1 : b.stem === '' ? 1 : a.stem.localeCompare(b.stem)));
    },
    visibleWidgets() {
      if (!this.needle) return this.widgets;
      return this.widgets.filter((widget) => this.widgetText(widget).includes(this.needle));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get('/api/impact_analytics/agent-pages/inventory/');
        this.pages = data.pages;
        this.widgets = data.widgets;
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to load the inventory';
      } finally {
        this.loading = false;
      }
    },
    toggle(key) {
      this.open = { ...this.open, [key]: !this.open[key] };
    },
    showWidget(slug) {
      const widget = this.widgets.find((w) => w.slug === slug);
      if (!widget) return;
      this.query = '';
      this.tab = 'widgets';
      this.open = { ...this.open, [`w-${widget.key}`]: true };
      this.$nextTick(() => document.getElementById(`widget-${widget.key}`)?.scrollIntoView({ block: 'start' }));
    },
    onPortal(page) {
      return !!page.shared && /^Portal(\/|$)/.test(page.stem || '');
    },
    visibility(page) {
      if (page.public) return { icon: 'globe', tone: 'text-primary-600', title: 'Public: everyone in the tenant can open it' };
      if (page.mine) return { icon: 'user', tone: 'text-gray-700', title: 'Just yours: private to you' };
      return { icon: 'lock', tone: 'text-gray-400', title: `Private to ${page.owner || 'its author'}` };
    },
    showPage(key) {
      this.query = '';
      this.tab = 'pages';
      this.open = { ...this.open, [key]: true };
      this.$nextTick(() => document.getElementById(`page-${key}`)?.scrollIntoView({ block: 'start' }));
    },
    pageText(page) {
      const sources = (list) => list.map((s) => `${s.kind} ${s.label} ${s.metrics.join(' ')} ${s.name || ''}`).join(' ');
      const placements = page.placements.map((p) => `${p.name} ${p.slug} ${sources(p.inputs)}`).join(' ');
      return `${page.title} ${page.owner} ${page.subject} ${page.stem} ${placements} ${sources(page.sources)}`.toLowerCase();
    },
    widgetText(widget) {
      const inputs = widget.inputs.map((i) => `${i.name} ${i.display_name} ${i.kinds.join(' ')}`).join(' ');
      const bound = widget.bound_sources.map((s) => `${s.kind} ${s.label}`).join(' ');
      const used = widget.used_by.map((u) => u.page).join(' ');
      return `${widget.name} ${widget.slug} ${widget.origin} ${inputs} ${bound} ${used}`.toLowerCase();
    },
    sourceBadges(widget) {
      const out = [];
      const seen = new Set();
      const add = (kind, label, accepts, input, parts) => {
        const generic = GENERIC_KINDS.has(kind);
        const named = !generic && !!label;
        const text = generic ? kind : (named ? label : (accepts ? `any ${kindLabel(kind)}` : kind));
        if (seen.has(text)) return;
        seen.add(text);
        const title = generic ? `Generic query (${kind}) on the page's metric or entity`
          : named ? `${kindLabel(kind)}: ${label}` : `Accepts any ${kindLabel(kind)}; no specific one is bound${input ? ` (input ${input})` : ''}`;
        out.push({ text, kind, generic, accepts: !generic && !named, title, parts: parts || [] });
      };
      widget.bound_sources.forEach((s) => add(s.kind, s.label, false, '', s.parts));
      if (!out.length) widget.inputs.forEach((i) => i.kinds.forEach((k) => add(k, '', true, i.name)));
      return out;
    },
    kindTone,
    queryText(query) {
      return Object.entries(query || {}).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ');
    },
    when(value) {
      return value ? new Date(value).toLocaleDateString() : '';
    },
    badge(origin) {
      const tone = {
        saved: 'bg-blue-100 text-blue-800', custom: 'bg-amber-100 text-amber-800', stock: 'bg-gray-200 text-gray-700',
        platform: 'bg-purple-100 text-purple-800', 'built-in': 'bg-gray-200 text-gray-700', html: 'bg-green-100 text-green-800',
        missing: 'bg-red-100 text-red-800',
      }[origin] || 'bg-gray-100 text-gray-700';
      return `text-xs rounded px-1.5 py-0.5 font-medium shrink-0 ${tone}`;
    },
  },
};
</script>
